import { useState } from "react";
import type { RefObject } from "react";
import {
  ChartLine,
  Home,
  MessageCircle,
  Plus,
  Target,
  UserRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../../lib/cn";

// App-level screen union. "library" and "settings" are reachable screens but
// NOT bottom-nav tabs: settings opens via the header avatar, library via its
// tab replacement in todo 19/21 flows and the Settings card shortcut.
export type Screen = "chat" | "today" | "progress" | "plan" | "library" | "settings" | "guide";
export type NavTabScreen = "chat" | "today" | "progress" | "plan";

// Visual order contract (todo 9): chat, today | central Quick Log | progress, plan.
export const SCREENS: { id: NavTabScreen; label: string; icon: LucideIcon }[] = [
  { id: "chat", label: "Chat", icon: MessageCircle },
  { id: "today", label: "Today", icon: Home },
  { id: "progress", label: "Progress", icon: ChartLine },
  { id: "plan", label: "Plan", icon: Target },
];

export const SCREEN_TEST_ID: Record<Screen, string> = {
  chat: "screen-chat",
  today: "screen-today",
  progress: "screen-progress",
  plan: "screen-plan",
  library: "screen-library",
  settings: "screen-settings",
  guide: "screen-guide",
};

export const TAB_TEST_ID: Record<NavTabScreen, string> = {
  chat: "tab-chat",
  today: "tab-today",
  progress: "tab-progress",
  plan: "tab-plan",
};

export const QUICK_LOG_TAB_TEST_ID = "tab-quick-log";
export const OPEN_SETTINGS_TEST_ID = "open-settings-btn";

export const SCREEN_TITLES: Record<Screen, string> = {
  chat: "BiteTrack",
  today: "Today",
  progress: "Progress",
  plan: "Plan",
  library: "Food Library",
  settings: "Settings",
  guide: "Guide",
};

export interface NavState {
  readonly screen: Screen;
  readonly quickLogOpen: boolean;
}

export type NavAction =
  | { readonly type: "select"; readonly screen: Screen }
  | { readonly type: "openSettings" }
  | { readonly type: "toggleQuickLog" };

export const INITIAL_NAV_STATE: NavState = { screen: "chat", quickLogOpen: false };

export function navReducer(state: NavState, action: NavAction): NavState {
  switch (action.type) {
    case "select":
      return { screen: action.screen, quickLogOpen: false };
    case "openSettings":
      return { screen: "settings", quickLogOpen: false };
    case "toggleQuickLog":
      return { ...state, quickLogOpen: !state.quickLogOpen };
  }
}

interface HeaderAvatarButtonProps {
  readonly initial: string | null;
  readonly pictureUrl?: string | null;
  readonly onOpenSettings: () => void;
}

export function HeaderAvatarButton({ initial, pictureUrl = null, onOpenSettings }: HeaderAvatarButtonProps) {
  // Store the failed URL itself so a later, different picture gets a fresh attempt.
  const [failedPictureUrl, setFailedPictureUrl] = useState<string | null>(null);
  const showPicture = pictureUrl !== null && pictureUrl !== failedPictureUrl;
  return (
    <button
      type="button"
      data-testid={OPEN_SETTINGS_TEST_ID}
      data-tour="avatar"
      aria-label="Open settings"
      onClick={onOpenSettings}
      className={cn(
        "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full",
        "bg-accent-light text-accent",
        "transition-all duration-200 ease-out active:scale-[0.98] select-none",
      )}
    >
      {showPicture ? (
        <img
          src={pictureUrl}
          alt=""
          className="h-9 w-9 rounded-full object-cover"
          onError={() => {
            console.debug("[Frontend][BottomNav] Avatar picture failed to load; falling back to initial");
            setFailedPictureUrl(pictureUrl);
          }}
        />
      ) : initial !== null ? (
        <span className="text-base font-semibold">{initial}</span>
      ) : (
        <UserRound size={22} strokeWidth={2} />
      )}
    </button>
  );
}

interface BottomNavProps {
  readonly currentScreen: Screen;
  readonly quickLogOpen: boolean;
  readonly onSelectScreen: (screen: NavTabScreen) => void;
  readonly onToggleQuickLog: () => void;
  readonly navRef: RefObject<HTMLElement | null>;
}

function NavTab({ id, label, icon: Icon, active, onSelect }: {
  readonly id: NavTabScreen;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly active: boolean;
  readonly onSelect: (screen: NavTabScreen) => void;
}) {
  return (
    <button
      type="button"
      data-testid={TAB_TEST_ID[id]}
      data-tour={`nav-${id}`}
      onClick={() => onSelect(id)}
      className={cn(
        "flex flex-col items-center gap-1 py-2 px-3 rounded-lg min-w-[60px] min-h-[44px]",
        "transition-all duration-200 ease-out active:scale-[0.98] select-none",
        active ? "text-accent" : "text-text-muted",
      )}
    >
      <Icon size={22} strokeWidth={active ? 2.5 : 2} />
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}

export function BottomNav({ currentScreen, quickLogOpen, onSelectScreen, onToggleQuickLog, navRef }: BottomNavProps) {
  const leftTabs = SCREENS.slice(0, 2);
  const rightTabs = SCREENS.slice(2);
  return (
    <nav ref={navRef} className="fixed bottom-0 inset-x-0 z-30 border-t border-border bg-surface pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto max-w-xl pt-2 px-2">
        <div className="flex justify-around items-center">
          {leftTabs.map((s) => (
            <NavTab key={s.id} id={s.id} label={s.label} icon={s.icon} active={currentScreen === s.id} onSelect={onSelectScreen} />
          ))}
          <button
            type="button"
            data-testid={QUICK_LOG_TAB_TEST_ID}
            data-tour="quick-log"
            aria-label="Quick log"
            aria-pressed={quickLogOpen}
            onClick={onToggleQuickLog}
            className={cn(
              "flex h-14 w-14 -translate-y-3 items-center justify-center rounded-full",
              "bg-accent text-white shadow-lg",
              "transition-all duration-200 ease-out active:scale-[0.98] select-none",
            )}
          >
            <Plus
              size={26}
              strokeWidth={2.5}
              className={cn("transition-transform duration-200 ease-out", quickLogOpen ? "rotate-45" : "")}
            />
          </button>
          {rightTabs.map((s) => (
            <NavTab key={s.id} id={s.id} label={s.label} icon={s.icon} active={currentScreen === s.id} onSelect={onSelectScreen} />
          ))}
        </div>
      </div>
    </nav>
  );
}
